import React from "react";
import { 
  HiOutlineChevronDown, 
  HiOutlineChevronRight, 
  HiOutlineCalendar, 
  HiOutlineClock, 
  HiOutlineExclamationCircle, 
  HiOutlineXCircle, 
  HiOutlineCheckCircle, 
  HiOutlineDotsHorizontal, 
  HiOutlineUsers,
  HiRefresh,
} from "react-icons/hi";
import { SiMicrosoftazure, SiJira, SiAsana } from "react-icons/si";
import { Dropdown, Badge } from "flowbite-react";
import { useDashboardService } from "../../../services/dashboard";
import { 
  getStatusIcon, 
  getStatusColor, 
  getStatusBorderColor,
  getProgressColor,
  formatStatusForDisplay
} from "./StatusUtils";
import {
  getSourceFromObject,
  getCompletionPercentage,
  Project,
  Epic,
  Feature,
  convertStatusFormat
} from "./SourceUtils";
import ProjectOptions from "./ProjectOptions";
import ProjectDetailCard from "./ProjectDetailCard";
import EpicDetailCard from "./EpicDetailCard";
import FeatureDetailCard from "./FeatureDetailCard";
import LoadingIndicator from "./LoadingIndicator";
import CacheInProgressIndicator from "./CacheInProgressIndicator";

// Interface for roadmap group from API
interface RoadmapGroup {
  id: string;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  progress: number;
  people_allocated: number;
  status: string;
  stats: {
    total_roadmaps: number;
    on_track_roadmaps: number;
    completed_roadmaps: number;
    delayed_roadmaps: number;
    blocked_roadmaps: number;
  };
  roadmaps: Roadmap[];
  last_updated?: string;
  updated_by?: string;
}

// Interface for roadmap from API
interface Roadmap {
  id: string;
  name: string;
  status: string;
  progress: number;
  start_date: string;
  end_date: string;
}

interface ProjectListProps {
  projects: Project[];
  selectedProject: Project | null;
  expandedProjectId: string | null;
  selectedEpic: Epic | null;
  selectedFeature: Feature | null;
  expandedEpicId: string | null;
  statusFilter: string;
  searchQuery: string;
  setShowCreateModal: (show: boolean) => void;
  toggleProject: (projectId: string) => void;
  toggleEpic: (epic: Epic) => void;
  selectEpic: (epic: Epic) => void;
  selectFeature: (feature: Feature) => void;
  openDashboard: (projectId: string) => void;
  openRoadmap: () => void;
  openTasks: () => void;
  heading?: string | React.ReactNode;
  currentRoadmapGroup?: RoadmapGroup | null;
  currentRoadmap?: any | null;
  currentRoadmapProject?: any | null;
  isLoading?: boolean;
  isProjectLoading?: boolean;
  isEpicLoading?: boolean;
  isFeatureLoading?: boolean;
  reloadRoadmapGroup?: (groupId: string) => void;
}

/**
 * Monday.com icon component
 */
const MondayIcon = () => (
  <div className="w-5 h-5 flex-shrink-0 relative">
    <div className="absolute top-0 left-0 w-2 h-2 rounded-full bg-[#ff3d57]"></div>
    <div className="absolute top-0 right-0 w-2 h-2 rounded-full bg-[#ff3d57]"></div>
    <div className="absolute bottom-0 left-0 w-2 h-2 rounded-full bg-[#ff3d57]"></div>
    <div className="absolute bottom-0 right-0 w-2 h-2 rounded-full bg-[#ff3d57]"></div>
  </div>
);

/**
 * CadencePro icon component
 */
const CadenceProIcon = () => (
  <img 
    src="/candencepro.png" 
    alt="CadencePro Logo" 
    className="h-5 w-5"
  />
);

const ProjectList: React.FC<ProjectListProps> = ({
  projects,
  selectedProject,
  expandedProjectId,
  selectedEpic,
  selectedFeature,
  expandedEpicId,
  statusFilter,
  searchQuery,
  setShowCreateModal,
  toggleProject,
  toggleEpic,
  selectEpic,
  selectFeature,
  openDashboard,
  openRoadmap,
  openTasks,
  heading,
  currentRoadmapGroup,
  currentRoadmap,
  currentRoadmapProject,
  isLoading = false,
  isProjectLoading = false,
  isEpicLoading = false,
  isFeatureLoading = false,
  reloadRoadmapGroup
}) => {
  const { refreshRoadmapGroup } = useDashboardService();

  // Helper to check if status is "cache_in_progress" safely across different types
  const isCacheInProgress = (status: string) => status.toLowerCase() === "cache_in_progress";

  // Helper function to get project logo based on the project source
  const getProjectLogo = (project: Project) => {
    // Get source from project
    const source = project.source?.toLowerCase() || "";
    
    // Map source to appropriate icon
    switch(source) {
      case "azure":
        return <SiMicrosoftazure className="h-5 w-5 text-blue-600" />;
      case "jira":
        return <SiJira className="h-5 w-5 text-blue-500" />;
      case "asana":
        return <SiAsana className="h-5 w-5 text-red-500" />;
      case "cadence":
        return <CadenceProIcon />;
      case "monday":
        return <MondayIcon />;
      default:
        // Default icon for projects without a specific source
        return <div className="h-5 w-5 flex items-center justify-center bg-indigo-500 rounded-full">
          <span className="text-xs font-bold text-white">{project.name.charAt(0)}</span>
        </div>;
    }
  };

  // Convert roadmap from API to Epic interface
  const convertRoadmapToEpic = (roadmap: Roadmap): import("./SourceUtils").Epic => {
    return {
      id: roadmap.id,
      name: roadmap.name,
      description: "",
      status: roadmap.status as any, // Keep the original status value
      progress: roadmap.progress, // Already in percentage
      startDate: new Date(roadmap.start_date).toLocaleDateString(),
      endDate: new Date(roadmap.end_date).toLocaleDateString(),
      features: [], // Features will be populated if needed
      blockers: 0
    };
  };

  const handleSelectFeature = (epicObject: Epic, featureObject: Feature) => {
    // If the same feature is clicked again, deselect it
    if (selectedFeature && selectedFeature.id === featureObject.id) {
      selectFeature({
        id: "",
        name: "",
        description: "",
        status: "not_started",
        progress: 0,
        startDate: "",
        endDate: "",
        tasks: [],
        blockers: 0,
        peopleAllocated: 0 // Add missing required property
      });
    } else {
      selectFeature(featureObject);
    }
  };

  // Helper function to scroll a project to the top
  const scrollProjectToTop = (projectId: string) => {
    setTimeout(() => {
      const projectHeader = document.getElementById(`project-header-${projectId}`);
      if (projectHeader) {
        // First try to find the direct scrollable parent container
        // This targets the specific structure in this component
        const mainContent = projectHeader.closest('.overflow-auto') || 
                            document.querySelector('.h-[calc(100vh-250px)]');
        
        if (mainContent && mainContent instanceof HTMLElement) {
          // Get the header/filter bar height to offset the scroll position
          const headerHeight = document.querySelector('.divide-y.divide-slate-200 > .p-4') || 
                              document.querySelector('.border-b.border-slate-200');
          const headerOffset = headerHeight ? headerHeight.getBoundingClientRect().height + 10 : 60;
          
          // Calculate the relative position within the scrollable container
          const containerTop = mainContent.getBoundingClientRect().top;
          const elementTop = projectHeader.getBoundingClientRect().top;
          const relativeTop = elementTop - containerTop;
          
          // Smooth scroll with animation
          const startPosition = mainContent.scrollTop;
          // Add a larger offset (50px) to position the header higher up
          const targetPosition = mainContent.scrollTop + relativeTop - headerOffset + 70;
          
          // Use requestAnimationFrame for smoother scrolling
          const startTime = performance.now();
          const duration = 300; // ms
          
          // Define animation function outside of block for strict mode compatibility
          const animateScroll = (currentTime: number) => {
            const elapsedTime = currentTime - startTime;
            const progress = Math.min(elapsedTime / duration, 1);
            const easeProgress = 0.5 - Math.cos(progress * Math.PI) / 2; // Ease in-out
            
            if (mainContent) { // Add null check for TypeScript
              mainContent.scrollTop = startPosition + (targetPosition - startPosition) * easeProgress;
            }
            
            if (progress < 1) {
              requestAnimationFrame(animateScroll);
            }
          };
          
          requestAnimationFrame(animateScroll);
        } else {
          // Fallback to window scroll
          window.scrollTo({
            top: projectHeader.offsetTop - 110, 
            behavior: 'smooth'
          });
        }
      }
    }, 300); // Slightly longer delay to ensure DOM is fully updated
  };

  return (
    <div className="divide-y divide-slate-200 dark:divide-slate-700">
      <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-[#2f3a4c]">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          {typeof heading === 'string' && (
            <>
              {/* Always use "Workspace Sources" as the common heading */}
              {heading}
            </>
          )}
          {typeof heading !== 'string' && heading}
        </h2>
      </div>

      {/* Display loading indicator when initial data is loading */}
      {isLoading && (
        <LoadingIndicator message="Loading project data..." />
      )}
      
      {/* Added overflow-x-auto to this container to make all projects scroll together */}
      <div className="overflow-x-auto">
        {/* Added a min-width to ensure horizontal scrolling on small screens */}
        <div className="min-w-[900px]">
          {!isLoading && projects
            .filter((project) => {
              // Apply status filter
              if (statusFilter !== "All Statuses" && project.status !== statusFilter) {
                return false;
              }
              // Apply search filter
              if (searchQuery && !project.name.toLowerCase().includes(searchQuery.toLowerCase())) {
                const hasMatchingEpic = project.epics.some(
                  (epic) =>
                    epic.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    epic.features.some((feature) =>
                      feature.name.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                );
                if (!hasMatchingEpic) {
                  return false;
                }
              }
              return true;
            })
            .map((project) => (
              <div key={project.id} className="bg-white dark:bg-[#1f2937]">
                
                <div
                  id={`project-header-${project.id}`}
                  className={`flex items-center justify-between p-4 ${
                    isCacheInProgress(project.status)
                      ? "cursor-not-allowed opacity-80" 
                      : "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700"
                  } transition-colors ${
                    expandedProjectId === project.id ? "bg-slate-50 dark:bg-[#1f2937]" : ""
                  }`}
                  onClick={() => {
                    // Disable click for cache_in_progress status
                    if (!isCacheInProgress(project.status)) {
                      toggleProject(project.id);
                      scrollProjectToTop(project.id);
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    {isCacheInProgress(project.status) ? (
                      <CacheInProgressIndicator size="md" customText="" />
                    ) : (
                      <>
                        {expandedProjectId === project.id ? (
                          <HiOutlineChevronDown className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <HiOutlineChevronRight className="h-5 w-5 text-slate-400" />
                        )}
                      </>
                    )}
                    <div className="flex items-center gap-2">
                      {getProjectLogo(project)}
                      <span className="font-medium text-slate-900 dark:text-white">{project.name}</span>
                    </div>
                    <Badge color="info" className={`ml-2 ${getStatusColor(project.status)}`}>
                      {isCacheInProgress(project.status) ? "Please wait" : formatStatusForDisplay(project.status)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    {!isCacheInProgress(project.status) && (
                      <>
                        <div className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-300">
                          <HiOutlineCalendar className="h-4 w-4" />
                          <span>
                            {project.startDate} - {project.endDate}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-300">
                          <HiOutlineUsers className="h-4 w-4" />
                          <span>{project.peopleAllocated} people</span>
                        </div>
                      </>
                    )}
                    <div 
                      onClick={(e) => e.stopPropagation()} 
                      className="bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 p-2 rounded-md cursor-pointer transition-colors"
                    >
                      {/* Pass the appropriate ID and last_updated data to ProjectOptions */}
                      <ProjectOptions 
                        projectId={currentRoadmapGroup?.id || project.id}
                        projectName={project.name}
                        onViewDetails={() => openDashboard(project.id)}
                        onDelete={() => {/* Add delete handler */}}
                        lastUpdated={currentRoadmapGroup?.last_updated}
                        updatedBy={currentRoadmapGroup?.updated_by}
                      />
                    </div>
                  </div>
                </div>

                {/* Project Content - Only show if expanded and not cache_in_progress */}
                {expandedProjectId === project.id && !isCacheInProgress(project.status) && (
                  <div className="border-t border-slate-200 dark:border-slate-700 w-full">
                    <div className="flex w-full">
                      {/* Sidebar with Epics */}
                      <div className="w-1/5 min-w-[250px] max-w-[250px] border-r border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex flex-col flex-shrink-0">
                        <div className="p-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                          <h3 className="font-medium text-sm text-slate-500 dark:text-slate-300">EPICS</h3>
                        </div>
                        <div className="overflow-auto h-[calc(100vh-300px)]">
                          {/* Show loading indicator when project data is loading */}
                          {isProjectLoading && (
                            <div className="p-4">
                              <LoadingIndicator message="Loading epics..." />
                            </div>
                          )}
                          
                          {!isProjectLoading && (
                            <div className="p-2 space-y-1">
                              {/* Use API data for Azure DevOps, otherwise use mock data */}
                              {currentRoadmapGroup 
                                ? currentRoadmapGroup.roadmaps && currentRoadmapGroup.roadmaps.map((roadmap: Roadmap) => {
                                    const epicFromRoadmap = convertRoadmapToEpic(roadmap);
                                    // Don't allow expansion if status is cache_in_progress
                                    const isEpicExpanded = !isCacheInProgress(epicFromRoadmap.status) && expandedEpicId === epicFromRoadmap.id;
                                    const roadmapDetails = isEpicExpanded && currentRoadmap ? currentRoadmap : null;
                                    
                                    return (
                                      <div key={epicFromRoadmap.id} className="space-y-1">
                                        <div
                                          id={`epic-${epicFromRoadmap.id}`}
                                          className={`flex items-center justify-between p-2 rounded-md ${
                                            isCacheInProgress(epicFromRoadmap.status)
                                              ? "cursor-not-allowed opacity-80" 
                                              : "cursor-pointer hover:bg-white dark:hover:bg-slate-700"
                                          } transition-colors ${
                                            selectedEpic?.id === epicFromRoadmap.id ? "bg-white dark:bg-slate-700 shadow-sm" : ""
                                          } ${getStatusBorderColor(epicFromRoadmap.status)}`}
                                        >
                                          <div 
                                            className="flex items-center gap-2 flex-1 min-w-0"
                                            onClick={() => {
                                              if (!isCacheInProgress(epicFromRoadmap.status)) {
                                                selectEpic(epicFromRoadmap);
                                              }
                                            }}
                                          >
                                            {isCacheInProgress(epicFromRoadmap.status) ? (
                                              <CacheInProgressIndicator size="sm" customText="" />
                                            ) : (
                                              <div 
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (!isCacheInProgress(epicFromRoadmap.status)) {
                                                    toggleEpic(epicFromRoadmap);
                                                  }
                                                }}
                                                className="cursor-pointer"
                                              >
                                                {isEpicExpanded ? (
                                                  <HiOutlineChevronDown className="h-4 w-4 text-slate-500" />
                                                ) : (
                                                  <HiOutlineChevronRight className="h-4 w-4 text-slate-500" />
                                                )}
                                              </div>
                                            )}
                                            <span className="text-sm text-slate-900 dark:text-white truncate" title={epicFromRoadmap.name}>
                                              {epicFromRoadmap.name}
                                            </span>
                                          </div>
                                          <Badge color="info" className={`ml-2 ${getStatusColor(epicFromRoadmap.status)}`}>
                                            {isCacheInProgress(epicFromRoadmap.status) ? "Data collecting" : formatStatusForDisplay(epicFromRoadmap.status)}
                                          </Badge>
                                        </div>

                                        {isEpicExpanded && (
                                          <div className="pl-6 pr-2 space-y-1">
                                            {/* Show epic loading state */}
                                            {isEpicLoading && (
                                              <div className="p-2">
                                                <LoadingIndicator message="Loading details..." />
                                              </div>
                                            )}
                                            
                                            {!isEpicLoading && (
                                              <>
                                                {/* For Azure DevOps, we can show projects from currentRoadmap as features */}
                                                {roadmapDetails && roadmapDetails.projects && roadmapDetails.projects.length > 0 ? (
                                                  roadmapDetails.projects.map((project: any) => {
                                                    const featureFromProject = {
                                                      id: project.id,
                                                      name: project.name,
                                                      description: project.description || "",
                                                      status: project.status,
                                                      startDate: new Date(project.start_date).toLocaleDateString(),
                                                      endDate: new Date(project.end_date).toLocaleDateString(),
                                                      tasksCompleted: project.stats?.completed || 0,
                                                      totalTasks: project.stats?.total || 0,
                                                      progress: project.progress, // Already in percentage
                                                      blockers: 0,
                                                      peopleAllocated: project.people_allocated || 0
                                                    };
                                                    
                                                    return (
                                                      <div
                                                        key={featureFromProject.id}
                                                        className={`p-2 rounded-md ${
                                                          isCacheInProgress(featureFromProject.status)
                                                            ? "cursor-not-allowed opacity-80" 
                                                            : "cursor-pointer hover:bg-white dark:hover:bg-slate-700"
                                                        } transition-colors ${
                                                          selectedFeature?.id === featureFromProject.id
                                                            ? "bg-white dark:bg-slate-700 shadow-sm"
                                                            : ""
                                                        } ${getStatusBorderColor(featureFromProject.status)}`}
                                                        onClick={() => {
                                                          // Disable click for cache_in_progress status
                                                          if (!isCacheInProgress(featureFromProject.status)) {
                                                            handleSelectFeature(epicFromRoadmap, featureFromProject);
                                                          }
                                                        }}
                                                      >
                                                        <div className="flex items-center justify-between">
                                                          <span className="text-sm text-slate-900 dark:text-white truncate" title={featureFromProject.name}>
                                                            {featureFromProject.name}
                                                          </span>
                                                          <Badge color="info" className={`ml-2 ${getStatusColor(featureFromProject.status)}`}>
                                                            {isCacheInProgress(featureFromProject.status) ?
                                                              <CacheInProgressIndicator size="sm" customText="" /> : 
                                                              getStatusIcon(featureFromProject.status)
                                                            }
                                                          </Badge>
                                                        </div>
                                                        
                                                        {/* Show progress from API or task completion - hide for cache_in_progress */}
                                                        {!isCacheInProgress(featureFromProject.status) && (
                                                          <div className="mt-2">
                                                            <div className="flex items-center gap-2">
                                                              <div className="h-1.5 rounded-full w-full bg-slate-200 dark:bg-slate-700">
                                                                <div 
                                                                  className={`h-full rounded-full ${getProgressColor(featureFromProject.progress || 0)}`}
                                                                  style={{ width: `${featureFromProject.progress || 0}%` }}
                                                                ></div>
                                                              </div>
                                                              <span className="text-xs text-slate-500 dark:text-slate-200">
                                                                {featureFromProject.progress || 0}%
                                                              </span>
                                                            </div>
                                                          </div>
                                                        )}
                                                      </div>
                                                    );
                                                  })
                                                ) : (
                                                  <div className="p-2 text-sm text-gray-500 dark:text-gray-400 italic">
                                                    No projects available
                                                  </div>
                                                )}
                                              </>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })
                                : project.epics.map((epic) => (
                                    <div key={epic.id} className="space-y-1">
                                      <div
                                        id={`epic-${epic.id}`}
                                        className={`flex items-center justify-between p-2 rounded-md ${
                                          isCacheInProgress(epic.status) 
                                            ? "cursor-not-allowed opacity-80" 
                                            : "cursor-pointer hover:bg-white dark:hover:bg-slate-700"
                                        } transition-colors ${
                                          selectedEpic?.id === epic.id ? "bg-white dark:bg-slate-700 shadow-sm" : ""
                                        } ${getStatusBorderColor(epic.status)}`}
                                      >
                                        <div 
                                          className="flex items-center gap-2 flex-1 min-w-0"
                                          onClick={() => {
                                            // Disable click for cache_in_progress status
                                            if (!isCacheInProgress(epic.status)) {
                                              // Only select the epic, don't toggle the dropdown
                                              selectEpic(epic);
                                            }
                                          }}
                                        >
                                          {isCacheInProgress(epic.status) ? (
                                            <CacheInProgressIndicator size="sm" customText="" />
                                          ) : (
                                            <div
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (!isCacheInProgress(epic.status)) {
                                                  toggleEpic(epic);
                                                }
                                              }}
                                              className="cursor-pointer shrink-0"
                                            >
                                              {expandedEpicId === epic.id ? (
                                                <HiOutlineChevronDown className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
                                              ) : (
                                                <HiOutlineChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
                                              )}
                                            </div>
                                          )}
                                          <span className="text-sm text-slate-900 dark:text-white truncate" title={epic.name}>
                                            {epic.name}
                                          </span>
                                        </div>
                                        <Badge color="info" className={`ml-2 ${getStatusColor(epic.status)}`}>
                                          {isCacheInProgress(epic.status) ? 
                                            <CacheInProgressIndicator size="sm" customText="" /> : 
                                            formatStatusForDisplay(epic.status)
                                          }
                                        </Badge>
                                      </div>

                                      {expandedEpicId === epic.id && !isCacheInProgress(epic.status) && (
                                        <div className="pl-6 pr-2 space-y-1">
                                          {/* Show epic loading state */}
                                          {isEpicLoading && (
                                            <div className="p-2">
                                              <LoadingIndicator message="Loading details..." />
                                            </div>
                                          )}
                                          
                                          {!isEpicLoading && (
                                            <>
                                              {epic.features.map((feature) => (
                                                <div
                                                  key={feature.id}
                                                  className={`p-2 rounded-md ${
                                                    isCacheInProgress(feature.status)
                                                      ? "cursor-not-allowed opacity-80" 
                                                      : "cursor-pointer hover:bg-white dark:hover:bg-slate-700"
                                                  } transition-colors ${
                                                    selectedFeature?.id === feature.id
                                                      ? "bg-white dark:bg-slate-700 shadow-sm"
                                                      : ""
                                                  } ${getStatusBorderColor(feature.status)}`}
                                                  onClick={() => {
                                                    // Disable click for cache_in_progress status
                                                    if (!isCacheInProgress(feature.status)) {
                                                      handleSelectFeature(epic, feature);
                                                    }
                                                  }}
                                                >
                                                  <div className="flex items-center justify-between">
                                                    <span className="text-sm text-slate-900 dark:text-white truncate" title={feature.name}>
                                                      {feature.name}
                                                    </span>
                                                    <Badge color="info" className={`ml-2 ${getStatusColor(feature.status)}`}>
                                                      {isCacheInProgress(feature.status) ?
                                                        <CacheInProgressIndicator size="sm" customText="" /> : 
                                                        getStatusIcon(feature.status)
                                                      }
                                                    </Badge>
                                                  </div>
                                                  {!isCacheInProgress(feature.status) && (
                                                    <div className="mt-2">
                                                      <div className="flex items-center gap-2">
                                                        <div className={`h-1.5 rounded-full w-full ${getProgressColor(getCompletionPercentage(
                                                          feature.tasksCompleted || 0,
                                                          feature.totalTasks || 0
                                                        ))}`}>
                                                        </div>
                                                        <span className="text-xs text-slate-500 dark:text-slate-200">
                                                          {getCompletionPercentage(
                                                            feature.tasksCompleted || 0,
                                                            feature.totalTasks || 0
                                                          )}
                                                          %
                                                        </span>
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Main Content Area */}
                      <div className="flex-1 overflow-hidden relative w-full">
                        <div className="h-[calc(100vh-250px)] overflow-y-auto w-full pt-3">
                          <div className="p-6 w-full bg-white dark:bg-[#0f172a]">
                            {/* Details panels */}
                            <div className="flex gap-6 mt-6">
                              {/* Project Details */}
                              {selectedProject && (
                                <ProjectDetailCard 
                                  project={selectedProject} 
                                  openDashboard={openDashboard} 
                                  currentRoadmapGroup={currentRoadmapGroup}
                                />
                              )}

                              {/* Epic Details */}
                              {selectedEpic && (
                                <EpicDetailCard
                                  epic={selectedEpic}
                                  selectFeature={selectFeature}
                                  openRoadmap={openRoadmap}
                                  currentRoadmap={currentRoadmap}
                                  projectName={selectedProject?.name || ""}
                                  openDashboard={openDashboard}
                                />
                              )}

                              {/* Feature Details */}
                              {isFeatureLoading && selectedFeature && (
                                <div className="flex-1 min-w-[350px] bg-white dark:bg-[#111928] rounded-lg shadow-md border border-slate-200 dark:border-slate-700 p-4">
                                  <LoadingIndicator message="Loading feature details..." />
                                </div>
                              )}
                              
                              {!isFeatureLoading && selectedFeature && (
                                <FeatureDetailCard
                                  feature={selectedFeature}
                                  openTasks={openTasks}
                                  currentProject={currentRoadmapProject}
                                  projectName={selectedProject?.name || ""}
                                  epicName={selectedEpic?.name || ""}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
};

export default ProjectList; 